<template>
  <div ref="root" class="user-menu">
    <button
      type="button"
      class="user-menu__trigger"
      :aria-expanded="open"
      aria-haspopup="menu"
      :title="displayName"
      @click="open = !open"
    >
      <span class="user-menu__avatar" aria-hidden="true">{{ initial }}</span>
      <span class="user-menu__name">{{ displayName }}</span>
      <span class="user-menu__chev" aria-hidden="true">▾</span>
    </button>

    <Transition name="user-menu">
      <div v-if="open" class="user-menu__panel" role="menu">
        <header class="user-menu__head">
          <div class="user-menu__head-name">{{ displayName }}</div>
          <div v-if="user?.email" class="user-menu__head-meta">{{ user.email }}</div>
          <div v-if="user?.role" class="user-menu__head-role">{{ user.role }}</div>
        </header>
        <ul class="user-menu__list">
          <slot :close="close" />
          <li>
            <button
              type="button"
              class="user-menu__item user-menu__item--danger"
              role="menuitem"
              @click="onSignOut"
            >
              {{ signOutLabel }}
            </button>
          </li>
        </ul>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
/**
 * Top-bar user menu — drops a dropdown panel with the user's identity at
 * the top, an arbitrary list of menu items via the default slot, and a
 * built-in "Sign out" item at the bottom.
 *
 * Generic on purpose: takes a `user` prop and emits `sign-out` rather
 * than reaching into a Pinia store. Each consuming app wires its own
 * auth store + router into the parent's handler.
 *
 * The default slot receives a `close` function so menu items can close
 * the dropdown after navigating.
 */
import { computed, onBeforeUnmount, onMounted, ref } from "vue";

interface MenuUser {
  name?: string;
  email?: string;
  role?: string | null;
}

const props = withDefaults(
  defineProps<{
    user: MenuUser | null;
    signOutLabel?: string;
  }>(),
  {
    signOutLabel: "Sign out",
  },
);

const emit = defineEmits<{
  (e: "sign-out"): void;
}>();

const open = ref(false);
const root = ref<HTMLElement | null>(null);

const displayName = computed(() => props.user?.name?.trim() || props.user?.email || "");
const initial = computed(() => {
  const source = props.user?.name?.trim() || props.user?.email || "?";
  return source.charAt(0).toUpperCase();
});

function close() {
  open.value = false;
}

function onDocumentClick(e: MouseEvent) {
  if (!open.value) return;
  if (root.value && !root.value.contains(e.target as Node)) {
    close();
  }
}
function onKey(e: KeyboardEvent) {
  if (e.key === "Escape" && open.value) close();
}

function onSignOut() {
  close();
  emit("sign-out");
}

onMounted(() => {
  document.addEventListener("click", onDocumentClick);
  document.addEventListener("keydown", onKey);
});
onBeforeUnmount(() => {
  document.removeEventListener("click", onDocumentClick);
  document.removeEventListener("keydown", onKey);
});
</script>

<style scoped>
.user-menu {
  position: relative;
}
.user-menu__trigger {
  display: inline-flex;
  align-items: center;
  gap: var(--ram-space-2);
  padding: var(--ram-space-1) var(--ram-space-3);
  background: var(--ram-color-surface-raised);
  color: var(--ram-color-text-primary);
  border: 1px solid var(--ram-color-border);
  border-radius: var(--ram-radius-full);
  font-family: inherit;
  font-size: var(--ram-text-sm);
  cursor: pointer;
  transition:
    transform var(--ram-duration-fast) var(--ram-ease-out),
    box-shadow var(--ram-duration-base) var(--ram-ease-out),
    border-color var(--ram-duration-base) var(--ram-ease-out);
}
.user-menu__trigger:hover {
  box-shadow: var(--ram-glow-action);
  border-color: color-mix(in oklab, var(--ram-color-action-500) 40%, var(--ram-color-border));
  transform: translateY(-1px);
}
.user-menu__avatar {
  width: 28px;
  height: 28px;
  display: inline-grid;
  place-items: center;
  background: linear-gradient(135deg, var(--ram-color-action-500), var(--ram-color-action-600));
  color: var(--ram-color-on-action);
  border-radius: 50%;
  font-weight: 600;
  font-size: var(--ram-text-sm);
}
.user-menu__name {
  max-width: 12ch;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.user-menu__chev {
  font-size: var(--ram-text-xl);
  color: var(--ram-color-text-muted);
}
.user-menu__panel {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 260px;
  background: var(--ram-color-surface-raised);
  border: 1px solid var(--ram-color-border);
  border-radius: var(--ram-radius-lg);
  box-shadow: var(--ram-shadow-lg);
  padding: var(--ram-space-2);
  z-index: var(--ram-z-dropdown);
}
[dir="rtl"] .user-menu__panel {
  right: auto;
  left: 0;
}
.user-menu__head {
  padding: var(--ram-space-3);
  border-bottom: 1px solid var(--ram-color-border);
  margin-bottom: var(--ram-space-2);
}
.user-menu__head-name {
  font-weight: 600;
  color: var(--ram-color-text-primary);
}
.user-menu__head-meta {
  font-size: var(--ram-text-xs);
  color: var(--ram-color-text-secondary);
  margin-top: 2px;
}
.user-menu__head-role {
  display: inline-block;
  margin-top: var(--ram-space-2);
  padding: 2px 8px;
  font-size: var(--ram-text-xs);
  background: color-mix(in oklab, var(--ram-color-action-500) 14%, transparent);
  color: var(--ram-color-link);
  border-radius: var(--ram-radius-full);
  letter-spacing: 0.04em;
  text-transform: uppercase;
  font-weight: 600;
}
.user-menu__list {
  list-style: none;
  margin: 0;
  padding: 0;
}
/* Local + slotted item styles. The Sign-out button below is in this
   component's template, so plain `.user-menu__item` reaches it. The
   "View profile" link (and any future menu item) comes from the
   parent's slot content, which under Vue 3 scoped CSS lives in the
   PARENT's data-v scope — `:slotted()` is the only selector that
   pierces through. Without it, slotted items inherit zero styling and
   visually fall out of alignment. */
.user-menu__item,
:slotted(.user-menu__item) {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 36px;
  margin: 0;
  padding: var(--ram-space-2) var(--ram-space-3);
  text-align: start;
  background: transparent;
  border: none;
  border-radius: var(--ram-radius-md);
  font-family: inherit;
  font-size: var(--ram-text-sm);
  font-weight: 500;
  line-height: 1.4;
  color: var(--ram-color-text-primary);
  text-decoration: none;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  transition: background var(--ram-duration-fast) var(--ram-ease-out);
  box-sizing: border-box;
}
.user-menu__item:hover,
.user-menu__item:focus-visible,
:slotted(.user-menu__item:hover),
:slotted(.user-menu__item:focus-visible) {
  background: var(--ram-color-surface-muted);
  outline: none;
}
.user-menu__item--danger {
  color: var(--ram-color-danger);
}
.user-menu__item--danger:hover,
.user-menu__item--danger:focus-visible {
  background: color-mix(in oklab, var(--ram-color-danger) 10%, transparent);
}

.user-menu-enter-active,
.user-menu-leave-active {
  transition:
    opacity var(--ram-duration-fast) var(--ram-ease-out),
    transform var(--ram-duration-fast) var(--ram-ease-out);
}
.user-menu-enter-from,
.user-menu-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
